import { useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  CreateApiKeyResponse,
  useCreateApiKeyMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { FlexColumn } from "../../../Common/__styles__/Layout";
import { ActionButton } from "../../../Common/Buttons/ActionButton";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import { Body } from "../../../Common/Typography";
import { Text } from "../../../Inputs/react-hook-form";
import { KeyRow } from "./__styles__/APIKeys";
import { validateApiKeyName } from "./validation";

type FormData = {
  name: string;
};

export const useAddAPIKeyModal = ({
  existingNames,
  refetch,
}: {
  existingNames: Array<string>;
  refetch: () => void;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <AddAPIKeyModal
          existingNames={existingNames}
          onClose={hideModal}
          refetch={refetch}
        />
      </Modal>
    ),
    [existingNames, refetch],
  );
  return [showModal, hideModal] as const;
};

export const AddAPIKeyModal = ({
  existingNames,
  onClose,
  refetch,
}: {
  existingNames: Array<string>;
  onClose: () => void;
  refetch: () => void;
}) => {
  const { addErrorToast } = useStatusToasts();
  const [createdKey, setCreatedKey] =
    useState<Maybe<CreateApiKeyResponse>>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({ mode: "onChange" });

  const [createAPIKey, { loading }] = useCreateApiKeyMutation();

  const onSubmit = async (formData: FormData) => {
    await createAPIKey({
      variables: { data: { name: formData.name.trim() } },
      onCompleted: data => {
        setCreatedKey(data.createAPIKey);
        refetch();
      },
      onError: error => {
        addErrorToast(error.message);
      },
    });
  };

  if (createdKey) {
    return (
      <CommonModal
        title="API key created"
        onCancel={onClose}
        primaryButtonText="Done"
        onSubmit={onClose}
        content={
          <FlexColumn gap="s">
            <Body size="default" type="emphasis">
              Save this key now — it won't be shown again.
            </Body>
            <KeyRow>
              <Body
                size="small"
                type="regular"
                as="code"
                style={{ fontFamily: "monospace" }}
              >
                {createdKey.key}
              </Body>
              <ActionButton
                icon="copy"
                aria-label="Copy to clipboard"
                onPress={() =>
                  void navigator.clipboard.writeText(createdKey.key)
                }
              />
            </KeyRow>
          </FlexColumn>
        }
      />
    );
  }

  return (
    <CommonModal
      title="Add API key"
      onCancel={onClose}
      onSubmit={handleSubmit(formData => void onSubmit(formData))}
      primaryButtonText="Create"
      loading={loading}
      disabled={!isValid}
      content={
        <Text
          label="Name"
          error={errors.name?.message}
          {...register("name", validateApiKeyName(existingNames))}
          required
        />
      }
    />
  );
};
